import { z } from 'zod';
import { CountryCodeSchema } from './geo';

/* ------------------------------------------------------------------ */
/* Capsico OS shell: global search, home summary, activity log (W3)    */
/* ------------------------------------------------------------------ */

export const SEARCH_RESULT_TYPES = [
  'town',
  'zone',
  'candidate',
  'staff',
  'config',
  'restaurant',
  'dish',
  'invoice',
] as const;
export const SearchResultTypeSchema = z.enum(SEARCH_RESULT_TYPES);
export type SearchResultType = z.infer<typeof SearchResultTypeSchema>;

export const SearchQuerySchema = z.object({
  q: z.string().trim().min(2, 'Type at least 2 characters').max(80),
  country: CountryCodeSchema.optional(),
});
export type SearchQuery = z.infer<typeof SearchQuerySchema>;

export const SearchResultSchema = z.object({
  type: SearchResultTypeSchema,
  id: z.string(),
  title: z.string(),
  subtitle: z.string().nullable(),
  /** Capsico OS path to open, e.g. /towns?town=<id>. */
  href: z.string(),
});
export type SearchResult = z.infer<typeof SearchResultSchema>;

export const SearchResponseSchema = z.object({
  query: z.string(),
  results: z.array(SearchResultSchema),
});
export type SearchResponse = z.infer<typeof SearchResponseSchema>;

/* Activity log: one feed over sign-in, geography/planning and config changes. */

export const ACTIVITY_SOURCES = [
  'auth',
  'geo',
  'config',
  'onboarding',
  'menu',
  'subscription',
] as const;
export const ActivitySourceSchema = z.enum(ACTIVITY_SOURCES);
export type ActivitySource = z.infer<typeof ActivitySourceSchema>;

export const ActivityEntrySchema = z.object({
  /** "<source>:<row id>" — unique across sources. */
  id: z.string(),
  source: ActivitySourceSchema,
  at: z.string(),
  actor: z.string().nullable(),
  actorName: z.string().nullable(),
  /** Machine action, e.g. "town.update", "sign_in", "config.set". */
  action: z.string(),
  /** Plain-language line for the table. */
  summary: z.string(),
  target: z.string().nullable(),
  href: z.string().nullable(),
  detail: z.record(z.string(), z.unknown()),
});
export type ActivityEntry = z.infer<typeof ActivityEntrySchema>;

export const ActivityQuerySchema = z.object({
  source: ActivitySourceSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** ISO time: return entries strictly older than this (for "Load more"). */
  before: z.iso.datetime({ offset: true }).optional(),
});
export type ActivityQuery = z.infer<typeof ActivityQuerySchema>;

export const ActivityPageSchema = z.object({
  entries: z.array(ActivityEntrySchema),
  /** Pass as `before` to load the next page; null when there is nothing older. */
  nextBefore: z.string().nullable(),
});
export type ActivityPage = z.infer<typeof ActivityPageSchema>;

/* Home */

export const AttentionItemSchema = z.object({
  id: z.string(),
  tone: z.enum(['warning', 'negative', 'info']),
  title: z.string(),
  detail: z.string(),
  href: z.string(),
});
export type AttentionItem = z.infer<typeof AttentionItemSchema>;

const StatusCounts = z.record(z.string(), z.number().int());

export const HomeSummarySchema = z.object({
  country: CountryCodeSchema.nullable(),
  towns: StatusCounts.nullable(),
  zones: StatusCounts.nullable(),
  candidates: StatusCounts.nullable(),
  staff: StatusCounts.nullable(),
  /** Outlets by onboarding status, plus "overdue" (reviews past their due time). */
  restaurants: StatusCounts.nullable(),
  config: z
    .object({
      keys: z.number().int(),
      overrides: z.number().int(),
      needsReview: z.number().int(),
      changesLast7Days: z.number().int(),
    })
    .nullable(),
  attention: z.array(AttentionItemSchema),
  recentActivity: z.array(ActivityEntrySchema),
});
export type HomeSummary = z.infer<typeof HomeSummarySchema>;

export const HomeQuerySchema = z.object({ country: CountryCodeSchema.optional() });
export type HomeQuery = z.infer<typeof HomeQuerySchema>;
